import MojitoCore from 'mojito/core';

import {resolveBackground} from '#core/utils/color-utils.js';
import {isMobile} from '#core/utils/context-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {registerTileSchema} from '#core/application/schema-registry.js';

import {generateSecondaryDropdown} from '#core/application/modules/tiles/dropdown/index.js';
import {generateEventTimingStatus} from '#core/application/modules/tiles/event-timing-status/index.js';
import {generateParticipants} from '#core/application/modules/tiles/participants/index.js';
import {generateMiniScoreboard} from '#core/application/modules/tiles/mini-scoreboard/index.js';
import {generateEarlyPayoutIndicator} from '#core/application/modules/tiles/early-payout-indicator/index.js';

import EVENT_LIST from './event-list.palette-schema.yaml';
import EVENT_DETAILS from './event-details.palette-schema.yaml';

const merge = MojitoCore.Presentation.uiStyle.merge;

registerTileSchema(EVENT_LIST);
registerTileSchema(EVENT_DETAILS);

export function generateEventList(TILE) {
    const {FONT} = TILE;

    const IS_MOBILE = isMobile();

    const EVENT_LIST_HEADER_WITH_SEPARATOR = IS_MOBILE
        ? TILE.EVENT_LIST_MOBILE_HEADER_WITH_SEPARATOR
        : TILE.EVENT_LIST_HEADER_WITH_SEPARATOR;

    const EVENT_LIST_HEADER_BG = IS_MOBILE ? TILE.EVENT_LIST_MOBILE_HEADER_BG : TILE.EVENT_LIST_HEADER_BG;

    const EVENT_LIST_MARKETS_DROPDOWN = IS_MOBILE
        ? TILE.EVENT_LIST_MOBILE_MARKETS_DROPDOWN
        : TILE.EVENT_LIST_MARKETS_DROPDOWN;

    return {
        header: {
            container: {
                itemSpacing: 8,
                style: {
                    minHeight: '49px',
                    _mobile_minHeight: '40px',
                    _mobile_padding: '0 8px',
                    _mobile_alignItems: 'center',
                    ...resolveBackground(EVENT_LIST_HEADER_BG),
                    ...(EVENT_LIST_HEADER_WITH_SEPARATOR
                        ? {
                              borderWidth: '0 0 1px 0',
                              borderStyle: 'solid',
                              borderColor: TILE.EVENT_LIST_HEADER_SEPARATOR_COLOR,
                          }
                        : {
                              borderWidth: 0,
                          }),
                },
            },
            title: {
                container: {
                    padding: '8px 0',
                },
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_NORMAL,
                    textTransform: 'capitalize',
                    ...TILE.EVENT_LIST_HEADER_TEXT,
                },
            },
            sortingIcon: icon(TILE.EVENT_LIST_HEADER_SORT_ICON_COLOR),
            sortEventsText: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    fontWeight: FONT.WEIGHT_SEMI_BOLD,
                    textTransform: 'capitalize',
                    ...TILE.EVENT_LIST_HEADER_SORT_TEXT,
                },
            },
            marketOptionsContainer: {
                container: {
                    style: {
                        _mobile_width: 'initial',
                        _mobile_minWidth: '100px',
                    },
                },
            },
            gameLineSwitcher: merge(generateSecondaryDropdown(EVENT_LIST_MARKETS_DROPDOWN), {
                style: {
                    rootContainer: {
                        height: IS_MOBILE ? 40 : 32,
                    },
                },
                controlButton: {
                    style: {
                        base: {
                            flexGrow: 1,
                        },
                    },
                },
            }),
            marketSwitcher: merge(generateSecondaryDropdown(EVENT_LIST_MARKETS_DROPDOWN), {
                style: {
                    rootContainer: {
                        height: IS_MOBILE ? 40 : 32,
                    },
                },
                controlButton: {
                    style: {
                        base: {
                            flexGrow: 1,
                        },
                    },
                },
            }),
            marketHeader: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...TILE.EVENT_LIST_MARKETS_HEADER_TEXT,
                },
            },
        },
        // moved it to separate function just for convenience
        container: {
            itemSpacing: 0, // override mojito defaults
            separator: {
                style: {
                    size: 0,
                },
            },
        },
        eventGroupsContainer: {
            itemSpacing: 0,
            separator: {
                style: {
                    color: TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR,
                    size: 1,
                },
            },
        },
        eventListGroup: generateEventListGroup(TILE),
        eventItem: generateEventListItem(TILE),
        eventItemsContainer: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_ITEM_SEPARATOR_COLOR),
            },
        },
        allEventsButton: {
            button: {
                style: {
                    base: {
                        ...resolveBackground(TILE.EVENT_LIST_ALL_EVENTS_CONTAINER_BG),
                        height: '40px',
                        borderStyle: 'solid',
                        borderWidth: '1px 0 0 0',
                        borderColor: TILE.EVENT_LIST_ALL_EVENTS_CONTAINER_SEPARATOR_COLOR,
                    },
                },
            },
            buttonLabel: {
                style: {
                    fontSize: FONT.SIZE_MEDIUM,
                    ...TILE.EVENT_LIST_ALL_EVENTS_LINK.SECONDARY_LINK_TEXT,
                },
            },
            buttonIcon: icon(TILE.EVENT_LIST_ALL_EVENTS_LINK.SECONDARY_LINK_ICON_COLOR),
        },
    };
}

function generateEventListGroup(TILE) {
    const {FONT} = TILE;

    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_GROUP_BG),
                padding: 0,
            },
        },
        headerButton: {
            style: {
                base: {
                    ...resolveBackground(TILE.EVENT_LIST_GROUP_HEADER_BG),
                    height: 40,
                    _mobile_height: 50,
                    _mobile_padding: '0px 8px',
                    padding: '0px 8px',
                },
            },
        },
        rotation: {
            image: {
                style: {
                    minWidth: '8px',
                    minHeight: '8px',
                    fill: TILE.EVENT_LIST_GROUP_HEADER_EXPAND_ICON_COLOR,
                },
            },
        },
        headerPrimaryLabel: {
            style: {
                fontSize: FONT.SIZE_MEDIUM,
                fontWeight: FONT.WEIGHT_SEMI_BOLD,
                ...TILE.EVENT_LIST_GROUP_NAME_TEXT,
            },
        },
        headerSecondaryLabel: {
            style: {
                fontSize: 12,
                ...TILE.EVENT_LIST_GROUP_CLASS_TEXT,
            },
        },
        headerSegmentLabel: {
            style: {
                fontSize: 13,
                ...TILE.EVENT_LIST_GROUP_MARKET_TEXT,
            },
            truncate: true,
        },
        earlyPayoutOffers: generateEventListOffers(TILE),
    };
}

export function generateEventListItem(TILE) {
    const EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT = {
        fontSize: 12,
        textAlign: 'center',
        lineHeight: 1,
        height: '100%',
        ...TILE.EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
    };

    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_LIST_ITEM_BG),
            },
        },
        eventDetailsRow: generateEventTimingStatus(TILE.EVENT_LIST_ITEM_TIMING_STATUS), // eventDetailsRow 🥴
        fallbackMarketNameContainer: {
            container: {
                style: {
                    top: '-20px',
                    height: '20px',
                    alignItems: 'flex-end',
                    padding: 0,
                },
            },
        },
        fallbackMarketName: {
            style: EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
        },
        fallbackMarketNameEndingInfo: {
            style: EVENT_LIST_ITEM_FALLBACK_MARKET_NAME_TEXT,
        },
        marketCountButton: {
            button: {
                style: {
                    base: {
                        fontSize: 13,
                        ...TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_TEXT,
                    },
                },
            },
            buttonIcon: {
                style: {
                    fill: TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_ICON_COLOR,
                },
            },
            buttonLabel: {
                truncate: true,
                container: {
                    maxWidth: '125px',
                },
            },
        },
        moreMarketsButton: {
            button: {
                style: {
                    base: {
                        fontSize: 13,
                        ...TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_TEXT,
                    },
                },
            },
            buttonIcon: {
                style: {
                    fill: TILE.EVENT_LIST_ITEM_MORE_MARKETS_BUTTON_ICON_COLOR,
                },
            },
            buttonLabel: {
                truncate: true,
            },
        },
        marketsSectionContainer: {
            container: {
                style: {
                    minHeight: 40,
                },
            },
        },
        itemDetails: merge(generateEventListItemDetails(TILE.EVENT_LIST_COMMON_ITEM_DETAILS), {
            container: {
                style: {
                    padding: '0 8px 0 0',
                    _mobile_width: '100%',
                },
            },
            _mobile_participantsAndScoreContainer: {
                style: {
                    justifyContent: 'space-between',
                },
            },
        }),
        itemDetailsGameLine: generateEventListItemDetails(TILE.EVENT_LIST_GAME_LINE_ITEM_DETAILS),
        eventNameLabel: {
            style: {
                ...TILE.EVENT_LIST_ITEM_OUTRIGHT_EVENT_NAME,
            },
        },
        genericMarketContent: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                },
            },
        },
        gameLineMarketContent: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                },
            },
        },
    };
}

export function generateEventListItemDetails(TILE) {
    return {
        container: {
            style: {
                ...resolveBackground(TILE.EVENT_DETAILS_BG),
            },
        },
        participants: merge(generateParticipants(TILE.EVENT_DETAILS_PARTICIPANTS), {
            container: {
                style: {
                    justifyContent: 'space-around',
                },
            },
            textWrapper: {
                style: {
                    justifyContent: 'center',
                },
            },
            startingPitcherName: {
                container: {
                    flexGrow: 0,
                },
            },
        }),
        scoreboard: merge(generateMiniScoreboard(TILE.EVENT_DETAILS_MINI_SCOREBOARD), {
            segmentIndicators: {
                text: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                },
            },
        }),
    };
}

export function generateEventListOffers(TILE) {
    const {FONT} = TILE;

    return {
        offersContainer: {
            style: {
                padding: '8px 0',
            },
        },
        offersText: {
            style: {
                fontSize: FONT.SIZE_XSMALL,
                ...TILE.EVENT_LIST_OFFERS_LABEL_TEXT,
            },
        },
        earlyPayoutIndicator: generateEarlyPayoutIndicator(TILE.EVENT_LIST_EARLY_PAYOUT_INDICATOR),
    };
}
